import { useState, useEffect, useRef, useCallback } from 'react';
import * as signalR from '@microsoft/signalr';
import { Agent, UserInfo, FeedEvent, ChatMessage, UiElement, ConnStatus } from '../types';
import { HUB_URL, SEED_ELEMENTS, DEMO_USERS, LANG_FLAGS, LANG_NAMES } from '../constants';
import { translateToEnglish } from '../services/translation';
import { getAccessToken } from '../services/cognitoAuth';

/** One semantic automation step — mirrors LpAutomationStep on the Angular side. */
export interface AutomationStep {
  action: 'click' | 'highlight' | 'tooltip' | 'focus' | 'scrollIntoView'
        | 'expand' | 'collapse' | 'open' | 'close' | 'select' | 'fill'
        | 'navigate' | 'wait' | 'speech' | 'toast';
  guid?: string;
  text?: string;
  value?: unknown;
  style?: 'gold' | 'redPulse';
  durationMs?: number;
  ms?: number;
  route?: string;
}

const uid = () => Math.random().toString(36).slice(2, 10);

// The hub moved off App Runner to ECS behind lp-alb-2. It must stay a direct
// ALB origin: API Gateway REST cannot carry the WebSocket upgrade.
const DIRECT_HUB_ORIGIN = "https://api2.development.logipulse.com/support";

// Exported so other parts of the app (e.g. CandidatesPanel) can build
// REST URLs against the same backend without duplicating these constants.
// Strip the /hubs/support suffix from HUB_ENDPOINT when using for REST.
export const INTERNAL_SECRET = "e4f1b7c2a9d3e6f0c4a7b2d9e1f6c8a3d5b7c9e2f1a4d6c8b0e3f7a2c5d9b1e6";

// The importer is a different service with a different secret. Sending the
// hub's value above to it returns 401.
export const IMPORTER_INTERNAL_SECRET = "9c4f2a7e6d1b8c3f5a0e2d9b7c6f1a3e8d5c2b7a9f0e1d6c3b8a4f2d7e9c1a5";
export const HUB_ENDPOINT = DIRECT_HUB_ORIGIN
  ? `${DIRECT_HUB_ORIGIN}/hubs/support`
  : HUB_URL;

const severityMap: Record<string, 'info' | 'warning' | 'error'> = {
  EVENT_CLICK: 'info', EVENT_UPLOAD_START: 'info', EVENT_UPLOAD_FAIL: 'error',
  EVENT_IMPORT_RETRY: 'warning', EVENT_WIZARD_STUCK: 'warning',
  EVENT_BACK_FORWARD_REPEAT: 'warning', EVENT_SCREEN_HOLD: 'warning',
};
const needsAttentionSet = new Set(['EVENT_UPLOAD_FAIL','EVENT_IMPORT_RETRY','EVENT_WIZARD_STUCK','EVENT_BACK_FORWARD_REPEAT']);

// Strip [FR] / [AR] prefix added by server when dictionary has no match
function stripLangPrefix(raw: string): { lang: string | null; text: string } {
  const m = raw.match(/^\[([A-Z]{2,5})\]\s*(.*)/s);
  if (m) return { lang: m[1].toLowerCase(), text: m[2].trim() };
  return { lang: null, text: raw };
}

export function useSignalR(agent: Agent | null, apiKey: string) {
  const connRef     = useRef<signalR.HubConnection | null>(null);
  const apiKeyRef   = useRef(apiKey);
  const agentRef    = useRef(agent);
  const pingRef     = useRef<ReturnType<typeof setInterval> | null>(null);
  const watchdogRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => { apiKeyRef.current = apiKey; });
  useEffect(() => { agentRef.current  = agent;  });

  const [status,     setStatus]     = useState<ConnStatus>('connecting');
  const [users,      setUsers]      = useState<UserInfo[]>([]);
  const [events,     setEvents]     = useState<FeedEvent[]>([]);
  const [chats,      setChats]      = useState<Record<string, ChatMessage[]>>({});
  const [unread,     setUnread]     = useState<Record<string, number>>({});
  const [uiElements, setUiElements] = useState<UiElement[]>(SEED_ELEMENTS);

  const unreadRef = useRef<Record<string, number>>({});

  const pushEvent = useCallback((ev: FeedEvent) =>
    setEvents(p => [ev, ...p].slice(0, 500)), []);

  const pushChat = useCallback((userId: string, msg: ChatMessage) => {
    setChats(p => ({ ...p, [userId]: [...(p[userId] ?? []), msg].slice(-150) }));
    unreadRef.current[userId] = (unreadRef.current[userId] ?? 0) + 1;
    setUnread({ ...unreadRef.current });
  }, []);

  const clearUnread = useCallback((userId: string) => {
    unreadRef.current[userId] = 0;
    setUnread({ ...unreadRef.current });
  }, []);

  // Stable login function — uses ref so no dependency issues
  const sendLogin = useCallback((conn: signalR.HubConnection) => {
    const a = agentRef.current;
    if (!a) return;
    conn.invoke('Login', { type: 'Login', userId: a.id, userName: a.name, lang: 'en', role: 'backOffice' })
        .catch(e => console.error('[Hub] Login failed:', e));
  }, []);

  // Handle user chat — auto-translate [XX] prefixed messages via Claude
  const handleUserChat = useCallback(async (userId: string, userName: string, rawText: string) => {
    const { lang: prefixLang, text: cleanText } = stripLangPrefix(rawText);

    if (!prefixLang) {
      pushChat(userId, { id: uid(), dir: 'in', from: userName, text: rawText, ts: new Date() });
      return;
    }

    const key = apiKeyRef.current;
    if (key?.startsWith('sk-ant-')) {
      try {
        const english = await translateToEnglish(cleanText, prefixLang, key);
        pushChat(userId, {
          id: uid(), dir: 'in', from: userName, text: english,
          translated: `Original (${prefixLang.toUpperCase()}): ${cleanText}`,
          usedLLM: true, ts: new Date(),
        });
        return;
      } catch (e) { console.warn('[useSignalR] Claude translation failed:', e); }
    }

    pushChat(userId, { id: uid(), dir: 'in', from: userName, text: `[${prefixLang.toUpperCase()}] ${cleanText}`, ts: new Date() });
  }, [pushChat]);

  useEffect(() => {
    if (!agent) return;

    const conn = new signalR.HubConnectionBuilder()
      .withUrl(HUB_ENDPOINT, {
        headers: DIRECT_HUB_ORIGIN ? { 'X-Internal-Secret': INTERNAL_SECRET } : {},
        // Proves this console is staff. SignalR sends it as a bearer header on
        // negotiate and as ?access_token= on the socket, which is the only form
        // a browser can attach to an upgrade.
        accessTokenFactory: async () => (await getAccessToken()) ?? '',
        skipNegotiation: false,
        transport: signalR.HttpTransportType.WebSockets | signalR.HttpTransportType.ServerSentEvents | signalR.HttpTransportType.LongPolling,
      })
      .withAutomaticReconnect([0, 1000, 3000, 5000, 15000, 30000])
      .configureLogging(signalR.LogLevel.Warning)
      .build();

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    conn.on('ReceiveMessage', (msg: any) => {
      switch (msg.type) {
        case 'LoginSuccess': setStatus('live'); break;

        case 'UserList':
          setUsers((msg.users ?? []).filter((u: UserInfo) => u.role === 0 || (u.role as any) === 'user'));
          break;

        case 'UserJoined':
          if (msg.user?.role === 0 || (msg.user?.role as any) === 'user') {
            setUsers(p => [msg.user, ...p.filter((u: UserInfo) => u.id !== msg.user.id)]);
            pushEvent({ id: uid(), type: 'user_joined', userId: msg.user.id, userName: msg.user.name,
              detail: `Connected · ${LANG_FLAGS[msg.user.lang] ?? '🌐'} ${LANG_NAMES[msg.user.lang] ?? msg.user.lang}`, ts: new Date() });
          }
          break;

        case 'UserLeft':
          // Mark offline — keep in list so chat history remains visible
          setUsers(p => p.map((u: UserInfo) => u.id === msg.userId ? { ...u, isOnline: false } : u));
          pushEvent({ id: uid(), type: 'user_left', userId: msg.userId, userName: msg.userName, detail: 'Disconnected', ts: new Date() });
          break;

        case 'ActivityEvent':
          setUsers(p => p.map((u: UserInfo) => u.id === msg.userId ? { ...u, currentPage: msg.page ?? u.currentPage } : u));
          pushEvent({ id: uid(), type: msg.event, eventType: msg.eventType, userId: msg.userId,
            userName: msg.userName, detail: msg.detail, page: msg.page, elementId: msg.elementId,
            clickCount: msg.clickCount, metadata: msg.metadata, ts: new Date() });
          if (msg.event === 'user_chat') {
            handleUserChat(msg.userId, msg.userName, msg.detail ?? '');
          }
          break;

        case 'TrackedEvent': {
          const evType = msg.eventType as string;
          const sev = (msg.severity ?? severityMap[evType] ?? 'info') as 'info' | 'warning' | 'error';
          const needsAt = msg.needsAttention ?? needsAttentionSet.has(evType);
          if (needsAt) setUsers(p => p.map((u: UserInfo) => u.id === msg.userId ? { ...u, hasAlert: true } : u));
          pushEvent({ id: uid(), type: 'tracked_event', eventType: evType, severity: sev, needsAttention: needsAt,
            userId: msg.userId, userName: msg.userName, detail: buildTrackedDetail(msg),
            page: msg.page, elementId: msg.elementId, metadata: msg.metadata, ts: new Date() });
          break;
        }

        case 'StuckAlert':
          setUsers(p => p.map((u: UserInfo) => u.id === msg.userId
            ? { ...u, hasAlert: true, stuckReason: msg.reason, idleSeconds: msg.idleSeconds ?? u.idleSeconds } : u));
          pushEvent({ id: uid(), type: msg.reason === 'LongIdle' ? 'idle_alert' : 'stuck_alert',
            userId: msg.userId, userName: msg.userName, detail: msg.description, page: msg.page,
            elementId: msg.elementId, clickCount: msg.clickCount, idleSeconds: msg.idleSeconds,
            reason: msg.reason, severity: 'warning', needsAttention: true, ts: new Date() });
          break;

        case 'UiTree':
          // Accept empty arrays — those are intentional broadcasts from
          // /api/ui-tree/clear and need to actually clear the UI. Before,
          // the .length > 0 guard meant the support console kept showing
          // stale data after a clear.
          if (Array.isArray(msg.elements)) setUiElements(msg.elements);
          break;
      }
    });

    // FIX: onreconnecting — mark offline but keep users in list
    conn.onreconnecting(() => {
      setStatus('connecting');
      setUsers(p => p.map(u => ({ ...u, isOnline: false })));
    });

    // FIX: onreconnected — re-login so server sends fresh UserList
    conn.onreconnected(() => {
      setStatus('live');
      sendLogin(conn);
    });

    // FIX: onclose — mark offline (watchdog below will reconnect)
    conn.onclose(() => {
      setStatus('offline');
      setUsers(p => p.map(u => ({ ...u, isOnline: false })));
    });

    conn.start()
      .then(() => { connRef.current = conn; sendLogin(conn); })
      .catch(err => {
        console.error('[SignalR] Connection failed:', err);
        setStatus('demo');
        setUsers(DEMO_USERS);
        pushEvent({ id: uid(), type: 'info', userId: 'sys', userName: 'System',
          detail: `Demo mode — server not reachable at ${HUB_ENDPOINT}`, ts: new Date() });
      });

    connRef.current = conn;

    // FIX: ping every 20s (server timeout = 60s — gives 3 chances)
    pingRef.current = setInterval(() => {
      if (connRef.current?.state === signalR.HubConnectionState.Connected)
        connRef.current.invoke('Ping').catch(() => {});
    }, 20_000);

    // FIX: watchdog — if withAutomaticReconnect exhausted retries, keep trying
    watchdogRef.current = setInterval(() => {
      const st = connRef.current?.state;
      if (st !== signalR.HubConnectionState.Connected &&
          st !== signalR.HubConnectionState.Connecting &&
          st !== signalR.HubConnectionState.Reconnecting) {
        connRef.current?.start().then(() => sendLogin(connRef.current!)).catch(() => {});
      }
    }, 30_000);

    // FIX: visibility change — reconnect when agent tab comes back into focus
    const onVisibility = () => {
      if (document.visibilityState === 'visible' &&
          connRef.current?.state !== signalR.HubConnectionState.Connected) {
        connRef.current?.start().then(() => sendLogin(connRef.current!)).catch(() => {});
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      if (pingRef.current)     clearInterval(pingRef.current);
      if (watchdogRef.current) clearInterval(watchdogRef.current);
      conn.stop();
    };
  }, [agent?.id]); // only re-run when agent changes

  const invoke = useCallback(async (method: string, payload?: unknown) => {
    const c = connRef.current;
    if (c?.state === signalR.HubConnectionState.Connected) {
      try { await c.invoke(method, payload); return true; }
      catch (e) { console.error(`[Hub] ${method}:`, e); }
    }
    return false;
  }, []);

  /**
   * Send an agent chat message.
   *
   * `translatedText` is the message ALREADY translated to the target user's
   * language by the console (Claude API via translateMessage / grammarAndTranslate,
   * or the built-in dictionary). The server prefers this over its own static
   * dictionary — which only knows a handful of canned phrases — so free text
   * like "Hello" reaches a French user as "Bonjour" instead of untranslated
   * English.
   */
  const sendAgentChat = useCallback((targetUserId: string, text: string, translatedText?: string) =>
    invoke('AgentChat', {
      type: 'AgentChat',
      agentId: agentRef.current?.id,
      agentName: agentRef.current?.name,
      targetUserId,
      text,
      // Only forward when it's a real translation (different from English).
      translatedText: translatedText && translatedText !== text ? translatedText : undefined,
    }), [invoke]);

  /**
   * Run a semantic automation sequence on the user's client (event-bus
   * protocol). Steps use the closed verb set:
   *   click | highlight | tooltip | focus | scrollIntoView | expand |
   *   collapse | open | close | select | fill | navigate | wait | speech | toast
   *
   * Example:
   *   sendAutomation(user.id, [
   *     { action: 'expand',    guid: MENU_GUID },
   *     { action: 'highlight', guid: TAB_GUID, text: 'Open the Packages tab' },
   *     { action: 'click',     guid: TAB_GUID },
   *     { action: 'wait',      ms: 1500 },
   *   ]);
   *
   * Per-step results arrive back as an 'AutomationProgress' message —
   * handle it in the ReceiveMessage switch to show live progress.
   */
  const sendAutomation = useCallback((targetUserId: string, steps: AutomationStep[], sequenceId = '') =>
    invoke('SendAutomation', {
      type: 'SendAutomation',
      agentId: agentRef.current?.id,
      agentName: agentRef.current?.name,
      targetUserId,
      sequenceId,
      steps,
    }), [invoke]);

  const sendFlow = useCallback((targetUserId: string, flowId: string) =>
    invoke('SendFlow', { type: 'SendFlow', agentId: agentRef.current?.id, targetUserId, flowId }), [invoke]);

  const sendHighlight = useCallback((targetUserId: string, sequence: string[], intervalMs = 1800) =>
    invoke('SendHighlight', { type: 'SendHighlight', agentId: agentRef.current?.id, targetUserId, sequence, intervalMs }), [invoke]);

  /**
   * Send a guided sequence that walks the user from a landing point
   * (side-menu or top-bar) down to the target element. Server-side
   * TreeSequenceBuilder walks the persisted UiTreeRegistry to compute
   * the ancestor path; we just pass the target id (and optionally
   * per-step custom text via stepTextOverrides).
   */
  const sendSequenceToElement = useCallback(
    (targetUserId: string, targetElementId: string,
     stepTextOverrides?: Record<string, string>) =>
      invoke('SendSequenceToElement', {
        type:               'SendSequenceToElement',
        agentId:            agentRef.current?.id,
        targetUserId,
        targetElementId,
        stepTextOverrides:  stepTextOverrides ?? null,
      }),
    [invoke]);

  return { status, users, events, chats, unread, uiElements, clearUnread,
           pushChat, pushEvent, sendAgentChat, sendAutomation, sendFlow, sendHighlight,
           sendSequenceToElement };
}

function buildTrackedDetail(msg: Record<string, unknown>): string {
  const evType = msg.eventType as string;
  const meta = msg.metadata as Record<string, unknown> | undefined;
  switch (evType) {
    case 'EVENT_CLICK':               return `Clicked "${msg.elementLabel ?? msg.elementId}" on ${msg.page}`;
    case 'EVENT_UPLOAD_START':        return `Upload started: ${meta?.fileName ?? 'file'} on ${msg.page}`;
    case 'EVENT_UPLOAD_FAIL':         return `Upload FAILED: ${meta?.fileName ?? 'file'} — ${meta?.error ?? 'unknown error'}`;
    case 'EVENT_IMPORT_RETRY':        return `Import retry on ${msg.page} (${meta?.context ?? ''})`;
    case 'EVENT_WIZARD_STUCK':        return `Wizard stuck at step "${meta?.stepLabel ?? meta?.stepIndex}" on ${msg.page}`;
    case 'EVENT_BACK_FORWARD_REPEAT': return `Back/forward repeat: ${(meta?.recentRoutes as string[])?.slice(-3).join(' ↔ ') ?? msg.page}`;
    case 'EVENT_SCREEN_HOLD':         return `Screen hold ${meta?.durationSeconds ?? 45}s on ${msg.page}`;
    default:                          return (msg.detail as string) ?? evType;
  }
}